import { Box, Grid, Stack, Tab, Tabs, useTheme } from '@mui/material';
import { DoorType } from 'graphql/generated/graphqlTypes';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useParams } from 'react-router';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import Loader from 'v2/components/Loader/Loader';
import useDeviceDetails from 'v2/hooks/useDeviceDetails/useDeviceDetails';
import NotFound from 'v2/pages/GeneralErrors/NotFound';
import SomethingWentWrong from 'v2/pages/GeneralErrors/SomethingWentWrong';
import AccessEventsTab from './components/AccessEventsTab';
import AccessOverviewTab from './components/AccessOverviewTab';
import DeviceDebug from './components/DeviceDebug';
import DeviceGroupsCard from './components/DeviceGroupsCard';
import DeviceInfo from './components/DeviceInfo';
import Header from './components/Header';
import UserGroupsCard from './components/UserGroupsCard';

export const createTabsHeader = (translation: Translation): string[] => [
  translation.access_overview,
  translation.events
];

const DeviceDetails: React.FC = () => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const tabsHeader = createTabsHeader(translation);
  const { deviceId } = useParams();
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { device, deviceLoading, locationsLoading, hasDeviceFetchError, retryFetchingDevice } = useDeviceDetails({
    handleFetchError,
    translation,
    deviceId: deviceId || ''
  });

  const handleChangeTab = (_event: React.SyntheticEvent, newIndex: number) => {
    setCurrentTabIndex(newIndex);
  };

  const isLoading = deviceLoading || locationsLoading;

  if (isLoading) {
    return (
      <Box justifyContent="center" alignContent="center" sx={{ width: '100%', height: '60vh' }}>
        <Loader />
      </Box>
    );
  }

  if (hasDeviceFetchError) {
    return <SomethingWentWrong onRetry={retryFetchingDevice} />;
  }

  if (!device) {
    return <NotFound />;
  }

  return (
    <Stack spacing={4}>
      <BreadcrumbMeta
        breadcrumb={{
          title: device.name,
          crumbs: [
            {
              text: translation.devices,
              path: '/devices'
            },
            {
              text: translation.all_devices,
              path: '/devices/overview'
            }
          ]
        }}
      />
      <Header
        deviceName={device.name}
        // TODO: not ready in api
        isDeleteDeviceButtonVisible={false}
        handleOpenDeleteDeviceModal={() => console.log('tood')}
      />
      <Grid container spacing={4} direction={{ xs: 'column', lg: 'row' }} wrap="nowrap">
        <Grid container direction="column" spacing={4} size={{ xs: 12, lg: 4 }} wrap="nowrap">
          <Grid container spacing={4}>
            <Grid size={12}>
              <DeviceInfo deviceId={device.id} />
            </Grid>
            {device?.type === DoorType.CloudControllerDoor && (
              <Grid size={12}>
                <DeviceDebug deviceId={device.id} />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={4}>
            <Grid size={12}>
              <DeviceGroupsCard deviceId={device.id} />
            </Grid>
            <Grid size={12}>
              <UserGroupsCard deviceId={device.id} />
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction="column" size={{ xs: 12, lg: 8 }} wrap="nowrap">
          <Box sx={{ borderBottom: 1, borderColor: theme.palette.divider }}>
            <Tabs value={currentTabIndex} onChange={handleChangeTab}>
              {tabsHeader.map((header, index) => (
                <Tab key={header} id={`tab-${index}`} label={header} />
              ))}
            </Tabs>
          </Box>
          {currentTabIndex === 0 && (
            <Grid container role="tabpanel" hidden={currentTabIndex !== 0} id={`tabpanel-${0}`}>
              <Box sx={{ pt: 3 }} style={{ flex: 1 }}>
                <AccessOverviewTab deviceId={device.id} />
              </Box>
            </Grid>
          )}
          {currentTabIndex === 1 && (
            <Grid container role="tabpanel" hidden={currentTabIndex !== 1} id={`tabpanel-${1}`}>
              <Box sx={{ pt: 3 }} style={{ flex: 1 }}>
                <AccessEventsTab deviceId={device.id} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};
export default DeviceDetails;
